import React, { useEffect, useState } from 'react';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import Sidebar from '../SidebarInAdminPanel/Sidebar';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { firestore } from '../../backend/firebaseConfig';
import { Doughnut, Bar } from 'react-chartjs-2';
import { fetchAllPosts } from "../../Controllers/postsController";
import { fetchAllRides } from "../../Controllers/rideController";

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartDataLabels
);

// Doughnut Chart Options (center text removed)
const doughnutOptions = {
  maintainAspectRatio: false,
  plugins: {
    datalabels: {
      display: false
    },
    legend: {
      position: 'bottom'
    }
  },
  cutout: '70%' // Optional: controls doughnut thickness
};

// Bar Chart Options
const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    datalabels: {
      display: false
    },
  },
  scales: {
    y: {
      ticks: {
        color: '#555',
        font: {
          size: 14,
        },
      },
    },
    x: {
      ticks: {
        color: '#555',
        font: {
          size: 14,
        },
      },
    },
  },
};

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [postCount, setPostCount] = useState(0);
  const [rideCount, setRideCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const querySnapshot = await getDocs(collection(firestore, 'users'));
        const usersData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsers(usersData);

        const posts = await fetchAllPosts();
        setPostCount(posts.length);

        const rides = await fetchAllRides();
        setRideCount(rides.length);
      } catch (error) {
        console.error("Dashboard Data Fetch Error:", error);
      }
    };

    fetchDashboardData();
  }, []);

  const totalVerified = users.filter(user => user.isverified).length;
  const totalUnverified = users.length - totalVerified;

  const getVerificationStatusIcon = (isverified) => {
    return isverified
      ? <FaCheckCircle className="text-green-500" />
      : <FaTimesCircle className="text-red-500" />;
  };

  const doughnutData = {
    labels: ['Verified', 'Unverified'],
    datasets: [
      {
        data: [totalVerified, totalUnverified],
        backgroundColor: ['#133E87', '#CBDCEB'],
        hoverOffset: 6,
      },
    ],
  };

  const barData = {
    labels: ['Total Users', 'Verified', 'Unverified'],
    datasets: [
      {
        label: 'User Stats',
        data: [users.length, totalVerified, totalUnverified],
        backgroundColor: ['#608BC1', '#133E87', '#F87171'],
        borderRadius: 6,
        barThickness: 30,
      },
    ],
  };

  const contentData = {
    labels: ['Posts', 'Rides'],
    datasets: [
      {
        label: 'Content Count',
        data: [postCount, rideCount],
        backgroundColor: ['#FFBB33', '#FF6666'],
        borderRadius: 6,
        barThickness: 30,
      },
    ],
  };

  return (
    <div className="flex flex-col md:flex-row bg-gradient-to-r from-[#0AD1C8] to-[#073235] min-h-screen">
      <Sidebar />
      <div className="flex-1 p-6 md:p-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-6 md:mb-8 text-white">Admin Dashboard</h1>

        <div className="bg-white p-4 md:p-6 rounded-xl shadow-md mb-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6">
            <div className="bg-blue-100 p-4 rounded-lg text-left">
              <h3 className="text-lg font-semibold text-blue-800">Total Users</h3>
              <p className="text-3xl font-bold text-blue-600">{users.length}</p>
            </div>
            <div className="bg-green-100 p-4 rounded-lg text-left">
              <h3 className="text-lg font-semibold text-green-800">Verified Users</h3>
              <p className="text-3xl font-bold text-green-600">{totalVerified}</p>
            </div>
            <div className="bg-red-100 p-4 rounded-lg text-left">
              <h3 className="text-lg font-semibold text-red-800">Unverified Users</h3>
              <p className="text-3xl font-bold text-red-600">{totalUnverified}</p>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Chart 1: Verification Breakdown */}
            <div className="bg-white rounded-xl shadow p-4">
              <h2 className="text-xl font-semibold text-gray-700 mb-3">Verification Breakdown</h2>
              <div className="h-56">
                <Doughnut data={doughnutData} options={doughnutOptions} />
              </div>
              <div className="flex justify-between items-center mt-4 text-sm text-gray-700">
                <span><strong>Total:</strong> {users.length}</span>
                <span className="text-green-600"><strong>✔ Verified:</strong> {totalVerified}</span>
                <span className="text-red-600"><strong>✘ Unverified:</strong> {totalUnverified}</span>
              </div>
            </div>

            {/* Chart 2: User Statistics */}
            <div className="bg-white rounded-xl shadow p-4">
              <h2 className="text-xl font-semibold text-gray-700 mb-3">User Statistics</h2>
              <div className="h-56">
                <Bar data={barData} options={barOptions} />
              </div>
              <div className="flex justify-between items-center mt-4 text-sm text-gray-700">
                <span><strong>Total:</strong> {users.length}</span>
                <span className="text-blue-700"><strong>✔ Verified:</strong> {totalVerified}</span>
                <span className="text-red-500"><strong>✘ Unverified:</strong> {totalUnverified}</span>
              </div>
            </div>

            {/* Chart 3: Posts vs Rides */}
            <div className="bg-white rounded-xl shadow p-4">
              <h2 className="text-xl font-semibold text-gray-700 mb-3">Posts vs Rides</h2>
              <div className="h-56">
                <Bar data={contentData} options={barOptions} />
              </div>
              <div className="flex justify-between items-center mt-4 text-sm text-gray-700">
                <span className="text-yellow-600"><strong>📦 Posts:</strong> {postCount}</span>
                <span className="text-pink-600"><strong>🚗 Rides:</strong> {rideCount}</span>
              </div>
            </div>
          </div>

          {/* User Table */}
          <div className="overflow-x-auto bg-white rounded-xl shadow-inner">
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Full Name</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">CNIC</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Verified</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map(user => (
                    <tr
                      key={user.id}
                      className={`hover:bg-opacity-75 cursor-pointer ${user.isverified ? 'bg-green-100' : 'bg-red-100'}`}
                      onClick={() => navigate(`/user-detail/${user.id}`)}
                    >
                      <td className="p-3 text-gray-900">{user.id}</td>
                      <td className="p-3 text-gray-900">{user.fullname}</td>
                      <td className="p-3 text-gray-900">{user.email}</td>
                      <td className="p-3 text-gray-900">{user.phone}</td>
                      <td className="p-3 text-gray-900">{user.cnic}</td>
                      <td className="p-3 text-gray-900 flex items-center justify-center">
                        {getVerificationStatusIcon(user.isverified)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
